import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { classNames } from "primereact/utils";

import { useForm } from "../useForm";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import AddProductToOrderForm from "../orders/modals/AddProductToOrderForm";
import OrderNotes from "../orders/modals/OrderNotes";
import SunDistributionDetailsSection from "./SunDistributionDetailsSection";
import SunOrderProductsTable from "./SunOrderProductsTable";
import { orderStatusOptions } from "../../../shared/utility";

const initialFValues = {
  id: 0,
  customer: "",
  customer_id: 0,
  created_by: null,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_date: new Date(),
  expected_date: null,
  po_number: "",
  notes: "",
  products: [],
  sun_customer: null,
  sun_customer_po_number: "",
  sun_bol_number: "",
  pallet_count: "",
  chep_pallet_count: "",
  total_freight_weight: "",
  appointment_number: "",
  temperature_recorder_number: "",
  carrier_name: "",
  is_prepaid: false,
  invoice_number: "",
  sun_quickbooks_invoice_id: null,
  seal_number: "",
  wfp_text: "",
  wfp_load_id_number: "",
  wfp_pickup_date: "",
  wfp_gate_in_gate_out_times: "",
};

export default function SunOrderForm(props: any) {
  const {
    customers,
    sunCustomers,
    getMyProducts,
    employeeOptions,
    isEdit,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    createOrderApiCall,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    updateOrderItemUnitCost,
    openSunShippingLabelReview,
    openSunBolReview,
    createSunOrderInvoice,
  } = props;

  const asyncRef = useRef<any>(null);
  const stepperRef = useRef<any>(null);
  const [asyncSelectValue, setAsyncSelectValue] = useState({ value: -1, label: "" });

  const validate = (fieldValues = values) => {
    const temp: any = { ...errors };

    const customer = fieldValues.customer ?? values.customer;
    temp.customer = null;
    if (customer === "" || customer == null) {
      temp.customer = "Please fill out field.";
    }

    const createdBy = fieldValues.created_by ?? values.created_by;
    temp.created_by = null;
    if (!createdBy) {
      temp.created_by = "Please fill out field.";
    }

    temp.po_number = null;
    if (!(fieldValues.po_number ?? values.po_number)) {
      temp.po_number = "Please fill out field.";
    }

    const orderDate = fieldValues.order_date ?? values.order_date;
    temp.order_date = null;
    if (!orderDate) {
      temp.order_date = "Please fill out field.";
    }

    temp.expected_date = null;

    temp.products = null;
    if ((fieldValues.products ?? values.products).length < 1) {
      temp.products = "Please add at least one product.";
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === null);
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
  }: any = useForm(
    initialFValues,
    false,
    validate
  );

  useEffect(() => {
    asyncRef.current?.focus();
  }, []);

  useEffect(() => {
    if (order) {
      setValues({
        ...order,
        status: orderStatusOptions.filter((option) => option.value === order.status)[0],
        is_prepaid: order.is_prepaid === "1" || order.is_prepaid === true,
      });
    }
  }, [order, setValues]);

  const findIndexForProduct = (productId: number) =>
    values.products.findIndex((element: any) => element.product_id === productId);

  const recalculateTotals = (products: any[]) => {
    const subtotal = products.reduce((sum, item) => {
      const total = parseFloat(item.total_dollar_amount ?? 0);
      return sum + (Number.isNaN(total) ? 0 : total);
    }, 0);

    return {
      subtotal_dollar_amount: subtotal.toFixed(2),
      total_dollar_amount: subtotal.toFixed(2),
    };
  };

  const addProductToOrder = async (addedProductValues: any) => {
    setAsyncSelectValue({ value: -1, label: "" });
    const quantity = parseFloat(addedProductValues.quantity ?? 0);
    const unitPrice = parseFloat(addedProductValues.unit_price ?? 0);
    const safeQuantity = Number.isNaN(quantity) ? 0 : quantity;
    const safeUnitPrice = Number.isNaN(unitPrice) ? 0 : unitPrice;
    const productToAdd = {
      id: -1,
      product_name: addedProductValues.name_en,
      product_id: addedProductValues.id,
      sun_product_id: addedProductValues.id,
      quantity: addedProductValues.quantity,
      unit_of_measure: addedProductValues.unit_of_measure,
      packing_size: addedProductValues.weight,
      individual_unit_size: addedProductValues.individual_unit_size,
      selling_unit_of_measure: addedProductValues.selling_unit_of_measure,
      plu: addedProductValues.plu,
      unit_price: safeUnitPrice,
      total_dollar_amount: (safeQuantity * safeUnitPrice).toFixed(2),
      sscc_hri_number: addedProductValues.sscc_hri_number ?? "",
    };

    if (findIndexForProduct(productToAdd.product_id) > -1) {
      showErrorModal("Product already in order, please edit it.");
      return;
    }

    let callResult: any = { success: false };
    let newProducts = [];

    if (isEdit) {
      callResult = await addProductToOrderApiCall(values.id, productToAdd);
      newProducts = [...values.products, callResult.data];
    } else {
      newProducts = [...values.products, productToAdd];
    }

    if (callResult.success || !isEdit) {
      setValues({
        ...values,
        products: newProducts,
        ...recalculateTotals(newProducts),
      });
      setEmptyModelOpen({ childComponent: null, open: false });
      setErrors((currentErrors: any) => ({ ...currentErrors, products: null }));
    }
  };

  const withCalculatedTotal = (item: any) => {
    const quantity = parseFloat(item.quantity ?? 0);
    const unitPrice = parseFloat(item.unit_price ?? 0);
    if (Number.isNaN(quantity) || Number.isNaN(unitPrice)) {
      return item;
    }
    return {
      ...item,
      total_dollar_amount: (quantity * unitPrice).toFixed(2),
    };
  };

  const updateItemLocally = (newData: any, index: number) => {
    const currentProducts = [...values.products];
    currentProducts[index] = newData;
    setValues({
      ...values,
      products: currentProducts,
      ...recalculateTotals(currentProducts),
    });
  };

  const onItemEditComplete = async (newData: any, index: number) => {
    const nextData = withCalculatedTotal(newData);

    if (isEdit) {
      const result = await onUpdateOrderItem(nextData.id, {
        quantity: nextData.quantity,
        sscc_hri_number: nextData.sscc_hri_number ?? "",
      });
      if (result.success) {
        updateItemLocally(nextData, index);
      }
      return;
    }

    updateItemLocally(nextData, index);
  };

  const onUnitPriceEditComplete = async (newData: any, index: number) => {
    const nextData = withCalculatedTotal(newData);

    if (isEdit) {
      const result = await updateOrderItemUnitCost(
        nextData.id,
        nextData.unit_price ?? 0
      );
      if (result.success) {
        updateItemLocally(nextData, index);
      }
      return;
    }

    updateItemLocally(nextData, index);
  };

  const makeDeleteOrderItemApiCall = async (itemIdInDB: number, productId: number) => {
    let onItemDeleted: any = { success: false };
    if (isEdit) {
      onItemDeleted = await deleteOrderItemApiCall(itemIdInDB);
    }

    if (onItemDeleted.success || !isEdit) {
      const filteredItems = values.products.filter(
        (item: any) => item.product_id !== productId
      );
      setValues({
        ...values,
        products: filteredItems,
        ...recalculateTotals(filteredItems),
      });
    }

    setEmptyModelOpen({ childComponent: null, open: false });
  };

  const askDeleteOrderItem = (product: any) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            makeDeleteOrderItemApiCall(product.id, product.product_id)
          }
          message={`Are you sure you want to delete ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const loadOptions = async (input: string) => {
    return getMyProducts(input);
  };

  const openNotesEditor = () => {
    setEmptyModelOpen({
      childComponent: (
        <OrderNotes
          orderNote={values.notes}
          confirmAction={(note: string) => {
            setValues({ ...values, notes: note });
            setEmptyModelOpen({ childComponent: null, open: false });
          }}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (values.id > 0) {
      updateOrderApiCall(values);
    } else {
      createOrderApiCall(values);
    }
  };

  const validateOrderDetailsStep = () => {
    const customerValid = values.customer !== "" && values.customer != null;
    const employeeValid = Boolean(values.created_by);
    const poNumberValid = Boolean(values.po_number);
    const orderDateValid = Boolean(values.order_date);
    setErrors((currentErrors: any) => ({
      ...currentErrors,
      customer: customerValid ? null : "Please fill out field.",
      created_by: employeeValid ? null : "Please fill out field.",
      po_number: poNumberValid ? null : "Please fill out field.",
      order_date: orderDateValid ? null : "Please fill out field.",
      expected_date: null,
    }));
    return customerValid && employeeValid && poNumberValid && orderDateValid;
  };

  const validateProductsStep = () => {
    const isValid = values.products.length > 0;
    setErrors((currentErrors: any) => ({
      ...currentErrors,
      products: isValid ? null : "Please add at least one product.",
    }));
    return isValid;
  };

  const goNext = (validator: any) => {
    if (validator()) {
      stepperRef.current?.nextCallback();
    }
  };

  const renderNotesCard = () => (
    <div className="card-custom p-3 mt-3">
      <div className="flex justify-between">
        <p className="text-sm text-gray-700 font-semibold">Notes</p>
        <button onClick={openNotesEditor} className="text-sm font-semibold main-purple">
          Edit
        </button>
      </div>
      <p className="text-sm text-gray-700 font-normal">
        {values.notes ? values.notes : "No notes"}
      </p>
    </div>
  );

  const renderStepNav = ({
    onBack,
    onNext,
    nextLabel = "Next",
    nextClassName = "",
  }: any) => (
    <div className="mt-6 flex items-center justify-between">
      <button
        type="button"
        className={classNames(
          "rounded-md px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
          onBack ? "" : "invisible"
        )}
        onClick={onBack}
      >
        Back
      </button>
      <button
        type="button"
        className={classNames(
          "rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
          nextClassName
        )}
        onClick={onNext}
      >
        {nextLabel}
      </button>
    </div>
  );

  const renderCreateStepper = () => (
    <div className="card-custom p-4">
      <Stepper ref={stepperRef} linear>
        <StepperPanel header="Order details">
          <SunDistributionDetailsSection
            values={values}
            errors={errors}
            handleInputChange={handleInputChange}
            customers={customers}
            employeeOptions={employeeOptions}
            sunCustomers={sunCustomers}
          />

          {renderNotesCard()}
          {renderStepNav({ onNext: () => goNext(validateOrderDetailsStep) })}
        </StepperPanel>

        <StepperPanel header="Products">
          <div className="space-y-4">
            <div className="flex rounded-md">
              <AsyncSelect
                cacheOptions
                loadOptions={loadOptions}
                defaultOptions
                options={[]}
                className="w-full"
                ref={asyncRef}
                onChange={(e: any) => {
                  setEmptyModelOpen({
                    childComponent: (
                      <AddProductToOrderForm
                        product={e}
                        confirmAction={addProductToOrder}
                        showUnitPrice
                        isEdit={false}
                        setOpen={() => {
                          setEmptyModelOpen({ open: false });
                          setAsyncSelectValue({ value: -1, label: "" });
                        }}
                      />
                    ),
                    open: true,
                  });
                }}
                value={asyncSelectValue}
              />
            </div>

            {errors.products ? <p className="font-medium text-red-500">{errors.products}</p> : null}

            <SunOrderProductsTable
              loading={false}
              values={values}
              onItemEditComplete={onItemEditComplete}
              onUnitPriceEditComplete={onUnitPriceEditComplete}
              askDeleteOrderItem={askDeleteOrderItem}
            />
          </div>
          {renderStepNav({
            onBack: () => stepperRef.current?.prevCallback(),
            onNext: () => goNext(validateProductsStep),
          })}
        </StepperPanel>

        <StepperPanel header="Review">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 card-custom p-3">
              <SunOrderProductsTable
                loading={false}
                values={values}
                onItemEditComplete={onItemEditComplete}
                onUnitPriceEditComplete={onUnitPriceEditComplete}
                askDeleteOrderItem={askDeleteOrderItem}
              />
            </div>

            <div className="space-y-4">
              <div className="card-custom p-3">
                <SunDistributionDetailsSection
                  values={values}
                  errors={errors}
                  handleInputChange={handleInputChange}
                  customers={customers}
                  employeeOptions={employeeOptions}
                  sunCustomers={sunCustomers}
                />
              </div>

              {renderNotesCard()}
            </div>
          </div>

          {renderStepNav({
            onBack: () => stepperRef.current?.prevCallback(),
            onNext: handleSubmit,
            nextLabel: values.id > 0 ? "Save Sun Order" : "Create Sun Order",
            nextClassName:
              "bg-green-600 hover:bg-green-700 focus-visible:outline-green-600",
          })}
        </StepperPanel>
      </Stepper>
    </div>
  );

  if (!isEdit) {
    return renderCreateStepper();
  }

  return (
    <div className="grid grid-cols-3 gap-3 h-full">
      <div className="col-span-2">
        <div className="card-custom p-3 py-5">
          <div className="flex rounded-md">
            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              defaultOptions
              options={[]}
              className="w-full"
              ref={asyncRef}
              onChange={(e: any) => {
                setEmptyModelOpen({
                  childComponent: (
                    <AddProductToOrderForm
                      product={e}
                      confirmAction={addProductToOrder}
                      showUnitPrice
                      isEdit={false}
                      setOpen={() => {
                        setEmptyModelOpen({ open: false });
                        setAsyncSelectValue({ value: -1, label: "" });
                      }}
                    />
                  ),
                  open: true,
                });
              }}
              value={asyncSelectValue}
            />
          </div>

          <SunOrderProductsTable
            loading={false}
            values={values}
            onItemEditComplete={onItemEditComplete}
            onUnitPriceEditComplete={onUnitPriceEditComplete}
            askDeleteOrderItem={askDeleteOrderItem}
            showLabelAction={Boolean(values.id > 0)}
            openSunShippingLabelReview={openSunShippingLabelReview}
          />
        </div>
      </div>

      <div className="col-span-1 space-y-5">
        <div className="card-custom p-3 space-y-3">
          <SunDistributionDetailsSection
            values={values}
            errors={errors}
            handleInputChange={handleInputChange}
            customers={customers}
            employeeOptions={employeeOptions}
            sunCustomers={sunCustomers}
          />

          <div className="mt-3 flex flex-wrap gap-2 py-4">
            <button
              type="submit"
              className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={handleSubmit}
            >
              Save
            </button>

            <button
              type="button"
              className="inline-flex justify-center rounded-md bg-slate-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
              onClick={() => openSunBolReview?.(values)}
            >
              BOL
            </button>

            {values.sun_quickbooks_invoice_id ? (
              <a
                href={`https://qbo.intuit.com/app/invoice?txnId=${values.sun_quickbooks_invoice_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                View Invoice
              </a>
            ) : (
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                onClick={async () => {
                  const result = await createSunOrderInvoice?.(values.id);
                  if (result?.success) {
                    setValues({
                      ...values,
                      invoice_number:
                        result.data?.invoice_number ?? values.invoice_number,
                      sun_quickbooks_invoice_id:
                        result.data?.sun_quickbooks_invoice_id ??
                        values.sun_quickbooks_invoice_id,
                    });
                  }
                }}
              >
                Create Invoice
              </button>
            )}
          </div>
        </div>

        {renderNotesCard()}
      </div>
    </div>
  );
}
